import { createContext, useContext, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, IconButton, Skeleton, TopBar, cn, useIsDesktop } from '@dezza/ui';
import { isTopLevelPath, matchNavItem } from '../app/nav';
import { MobileBell } from '../app/notifications';
import { MenuAvatarButton } from '../app/SideMenu';
import { TopActions } from '../app/TopActions';

const ToneContext = createContext<'dark' | 'light'>('dark');
/** Tom da barra superior atual: ações devem usar variant 'on-dark' (celular) ou 'ghost' (desktop). */
export const usePageTone = () => useContext(ToneContext);

export interface PageProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** true = voltar no histórico; string = rota de destino. Sem back, mostra o menu (celular). */
  back?: boolean | string;
  actions?: ReactNode;
  /** Abas/filtros logo abaixo da barra (ex.: <Tabs/> CADASTRO | ESTOQUE). */
  header?: ReactNode;
  children: ReactNode;
  /** Conteúdo ocupa a largura toda, sem padding (listas, PDV). */
  bleed?: boolean;
  /** Largura máxima do conteúdo. */
  width?: 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

const WIDTHS = { md: 'max-w-3xl', lg: 'max-w-5xl', xl: 'max-w-7xl', full: 'max-w-none' };

/** Título do desktop: trilha até o módulo (em telas internas) e o nome da página. */
function DesktopTitle({ title, crumb }: { title: ReactNode; crumb: { to: string; label: string } | null }) {
  const heading =
    typeof title === 'string' || typeof title === 'number' ? (
      <h1 className="truncate text-[17px] font-semibold tracking-[-0.015em] text-ink">{title}</h1>
    ) : (
      title
    );
  if (!crumb) return heading;
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <nav aria-label="Trilha" className="hidden shrink-0 items-center gap-1.5 xl:flex">
        <Link
          to={crumb.to}
          className="rounded-md text-[15px] font-medium text-ink-3 outline-none transition-colors hover:text-ink focus-visible:ring-2 focus-visible:ring-ring"
        >
          {crumb.label}
        </Link>
        <ChevronRight className="size-4 text-ink-3/70" aria-hidden="true" />
      </nav>
      <div className="min-w-0">{heading}</div>
    </div>
  );
}

/**
 * Moldura das telas. Desktop: barra clara com trilha, título, ações da página e as ações globais
 * (busca, Nova venda, notificações, tema e conta). Celular: barra escura compacta com o logo da loja
 * (abre o menu) ou "voltar", título, ações da página e o sino nas telas de primeiro nível.
 */
export function Page({
  title,
  subtitle,
  back,
  actions,
  header,
  children,
  bleed,
  width = 'lg',
  className,
}: PageProps) {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const tone = isDesktop ? 'light' : 'dark';
  const btn = tone === 'dark' ? 'on-dark' : 'ghost';

  let left: ReactNode = null;
  if (back) {
    left = (
      <IconButton
        aria-label="Voltar"
        variant={btn}
        onClick={() => (typeof back === 'string' ? navigate(back) : navigate(-1))}
      >
        <ChevronLeft />
      </IconButton>
    );
  } else if (!isDesktop) {
    left = <MenuAvatarButton className="-ml-0.5" />;
  }

  const module = back ? matchNavItem(location.pathname) : null;
  const crumb = module && module.label !== title ? { to: module.to, label: module.label } : null;

  const right = isDesktop ? (
    <>
      {actions ? (
        <>
          <div className="flex items-center gap-1">{actions}</div>
          <span aria-hidden="true" className="mx-2 h-6 w-px bg-line-strong" />
        </>
      ) : null}
      <TopActions />
    </>
  ) : (
    <>
      {actions}
      {!back && isTopLevelPath(location.pathname) && location.pathname !== '/notificacoes' ? (
        <MobileBell />
      ) : null}
    </>
  );

  return (
    <ToneContext.Provider value={tone}>
      <TopBar
        variant={tone}
        left={left}
        title={isDesktop ? <DesktopTitle title={title} crumb={crumb} /> : title}
        subtitle={subtitle}
        right={right}
      >
        {header}
      </TopBar>
      <div className={cn('mx-auto w-full', WIDTHS[width], !bleed && 'px-4 py-4 sm:px-6 sm:py-6', className)}>
        {children}
      </div>
    </ToneContext.Provider>
  );
}

/** Botão de ação da barra superior que se adapta ao tom (escuro/claro). */
export function PageAction({
  icon,
  label,
  onClick,
  badge,
  showLabel,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  badge?: number | boolean;
  /** Mostra o texto ao lado do ícone no desktop. */
  showLabel?: boolean;
  disabled?: boolean;
}) {
  const tone = usePageTone();
  const isDesktop = useIsDesktop();
  if (showLabel && isDesktop) {
    return (
      <Button
        variant={tone === 'dark' ? 'on-dark' : 'soft'}
        size="sm"
        leftIcon={icon}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
    );
  }
  return (
    <IconButton
      aria-label={label}
      title={label}
      variant={tone === 'dark' ? 'on-dark' : 'ghost'}
      onClick={onClick}
      badge={badge}
      disabled={disabled}
    >
      {icon}
    </IconButton>
  );
}

/**
 * Esqueleto genérico de página (use como fallback do Suspense das rotas em vez do indicador girando).
 * `variant`: lista (linhas) ou painel (indicadores + bloco grande).
 */
export function PageSkeleton({ variant = 'list' }: { variant?: 'list' | 'dashboard' }) {
  const isDesktop = useIsDesktop();
  return (
    <div role="status" aria-label="Carregando" className="animate-fade-in">
      <div
        className={cn(
          'flex h-14 items-center px-4',
          isDesktop ? 'border-b border-line bg-surface' : 'bg-dark',
        )}
      >
        <Skeleton className={cn('h-4 w-32', !isDesktop && 'bg-white/10')} />
      </div>
      <div className="mx-auto w-full max-w-5xl space-y-3 px-4 py-4 sm:px-6 sm:py-6">
        {variant === 'dashboard' ? (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-card" />
              ))}
            </div>
            <Skeleton className="h-72 rounded-card" />
          </>
        ) : (
          <>
            <Skeleton className="h-11 w-full rounded-control" />
            <div className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                  <Skeleton className="size-11 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-1/2" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Página simples de "em construção" (usada por rotas ainda não implementadas). */
export function ComingSoon({ title }: { title: string }) {
  return (
    <Page title={title}>
      <div className="py-16 text-center text-ink-2">Esta tela está sendo preparada.</div>
    </Page>
  );
}
